// 韩文标签资料 「붙여넣기 자동 인식」(2026-10-07 YZY) — 순수 함수(의존성 없음). 테스트: node code-vue/src/views/backend/Logis/__tests__/coupassLabelParse.test.mjs
// 한 덩어리로 붙여넣은 한글표시사항을 칸별로 나눈다.
//   「<코드> <상품 표시명> 라벨 : 값 라벨 : 값 …」
//   - 맨 앞 토큰이 숫자 섞인 코드면 code(10자리=화주주문번호, 14자리=상품주문번호, 그 밖=바코드)
//   - 첫 라벨 앞까지 = 상품 표시명(title)
//   - 라벨은 아래 사전(별칭)으로 칸 키에 맞춘다. 공백·전각콜론(：)·줄바꿈 변형 허용. 사전에 없는 라벨은 others(→ 「기타」 칸)
// 칸 키는 韩文标签资料 폼·서버 LABEL_FOOD_KEYS / LABEL_KC_KEYS 와 같다.

// 칸 키 → 별칭(붙여넣기에서 보이는 라벨). 같은 문자열이 두 키에 들어가지 않게 할 것
export const LABEL_ALIASES = {
  '제품명': ['제품명', '상품명', '품명', '제품 명칭', '상품명칭'],
  '상품유형': ['식품유형', '식품의 유형', '식품의유형', '상품유형', '제품유형'],
  '수입원/판매원': ['수입원/판매원', '수입원 및 판매원', '수입판매원', '수입·판매원', '수입원/판매처', '수입원', '판매원', '수입자', '수입업소', '수입사', '판매자', '판매사'],
  '반품교환업무 소재지': ['반품교환업무 소재지', '반품교환업무소재지', '반품/교환업무 소재지', '반품교환 소재지', '반품/교환 소재지', '반품교환 주소', '반품/교환 주소', '반품교환', '반품/교환', '소재지', '주소', '고객센터', '연락처', '전화번호'],
  '제조원': ['제조원', '제조자', '제조사', '제조업소', '제조회사', '제조업체', '생산자', '생산업체'],
  '제조국': ['제조국', '제조국가', '원산지', '생산국'],
  '내용량': ['내용량', '순중량', '중량', '용량', '수량', '구성'],
  '원료명및성분명(재질)': ['원료명및성분명(재질)', '원료명 및 성분명(재질)', '원료명 및 성분명', '원재료명 및 함량', '원재료명및함량', '원재료명', '원재료', '원료명', '성분명', '성분', '재질', '소재', '재료'],
  '소비기한': ['소비기한', '유통기한', '유통기간', '사용기한', '품질유지기한'],
  '보관방법': ['보관방법', '보관 및 취급방법', '보관및취급방법', '보관법'],
  '사용시주의사항': ['사용시주의사항', '사용시 주의사항', '사용상주의사항', '사용상 주의사항', '사용상의 주의사항', '취급시주의사항', '취급시 주의사항', '주의사항'],
  '모델명': ['모델명', '모델번호', '모델', '품번'],
  'KC인증번호': ['KC인증번호', 'KC 인증번호', 'KC인증 번호', '안전인증번호', '안전확인번호', '공급자적합성확인번호', '인증번호', 'KC번호', 'KC인증'],
  '제조연월': ['제조연월', '제조년월', '제조연월일', '제조년월일', '제조일자', '제조일'],
  '사용기준': ['사용기준', '사용연령', '사용 연령', '권장연령', '권장 연령', '사용대상'],
  '기타': ['기타']
};

// 식검(식품)·KC 어느 쪽 표시사항인지 가늠하는 칸
const FOOD_HINT_KEYS = ['상품유형', '소비기한', '보관방법'];
const KC_HINT_KEYS = ['KC인증번호', '모델명', '제조연월'];

const compact = s => String(s).replace(/\s+/g, '');
// 별칭을 공백 뺀 모양으로 펴고, 긴 것부터 맞춘다(「사용시주의사항」이 「주의사항」보다 먼저)
const ALIAS_LIST = Object.keys(LABEL_ALIASES)
  .reduce((acc, key) => acc.concat(LABEL_ALIASES[key].map(a => ({ key, c: compact(a).toLowerCase() }))), [])
  .sort((a, b) => b.c.length - a.c.length);

const SEP_CHARS = ' \t\n\r,;|·•/[](){}<>「」【】-';

// colon 바로 앞 원문에서 라벨이 시작하는 위치를 찾는다 — 공백을 건너뛰며 별칭 글자 수만큼 뒤로 간다
function backToStart(text, end, nChars) {
  let i = end;
  let n = 0;
  while (i > 0 && n < nChars) {
    i--;
    if (!/\s/.test(text[i])) n++;
  }
  return n === nChars ? i : -1;
}

function findLabelBefore(text, colonIdx, minStart) {
  // 콜론 앞 공백 무시
  let end = colonIdx;
  while (end > minStart && /\s/.test(text[end - 1])) end--;
  const before = text.slice(Math.max(minStart, end - 40), end);
  const cBefore = compact(before).toLowerCase();
  for (const a of ALIAS_LIST) {
    if (!cBefore.endsWith(a.c)) continue;
    const start = backToStart(text, end, a.c.length);
    if (start < minStart) continue;
    // 두 글자 이하 별칭(재질·성분·주소 …)은 앞이 끊겨 있어야 한다(「스테인레스재질」 같은 값 속 낱말 오인 방지)
    const prev = start > 0 ? text[start - 1] : ' ';
    if (a.c.length <= 2 && SEP_CHARS.indexOf(prev) < 0) continue;
    return { key: a.key, start, label: text.slice(start, end).replace(/\s+/g, ' ').trim() };
  }
  // 사전에 없는 라벨 — 콜론 바로 앞 낱말 하나(글자만, 숫자 섞이면 라벨이 아니다: 「10:30」·「010-…」)
  let s = end;
  while (s > minStart && !/\s/.test(text[s - 1])) s--;
  const word = text.slice(s, end).replace(/^[-·•,;|]+/, '');
  if (word && word.length <= 15 && /[가-힣A-Za-z]/.test(word) && !/\d/.test(word)) {
    return { key: null, start: end - word.length, label: word };
  }
  return null;
}

function cleanValue(v) {
  return String(v).replace(/\s+/g, ' ').replace(/^[\s,;|/·•-]+/, '').replace(/[\s,;|/·•]+$/, '').trim();
}

// 맨 앞 토큰이 코드인가 — 한글 없음 + 숫자 포함 + 6자 이상(R242166510002, 2610070856, 26100708560001, S0038472580792 …)
function detectCode(token) {
  const t = String(token || '').trim();
  if (!t || /[가-힣]/.test(t) || !/\d/.test(t) || t.length < 6 || t.length > 40) return null;
  if (!/^[A-Za-z0-9\-_]+$/.test(t)) return null;
  if (/^\d{10}$/.test(t)) return { code: t, codeType: 'order' };
  if (/^\d{14}$/.test(t)) return { code: t, codeType: 'item' };
  if (/^\d{16,19}$/.test(t)) return { code: t, codeType: 'order1688' }; // 1688 주문번호 — 바코드 칸에 넣지 않는다
  return { code: t, codeType: 'barcode' };
}

/**
 * @param {string} raw 붙여넣은 글
 * @returns {{ code:string, codeType:''|'order'|'item'|'barcode', title:string, fields:Object<string,string>,
 *             others:{label:string,value:string}[], items:{label:string,key:string|null,value:string}[], section:'food'|'kc'|'' }}
 */
export function parseLabelText(raw) {
  const text = String(raw == null ? '' : raw).replace(/\r\n?/g, '\n').replace(/[：﹕]/g, ':').replace(/\u00a0/g, ' ');
  const out = { code: '', codeType: '', title: '', fields: {}, others: [], items: [], section: '' };
  if (!text.trim()) return out;

  // 라벨 위치 모으기(콜론마다 앞쪽 라벨을 찾는다). 라벨이 아닌 콜론은 값의 일부로 둔다
  const marks = [];
  let minStart = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== ':') continue;
    const found = findLabelBefore(text, i, minStart);
    if (!found) continue;
    marks.push({ key: found.key, label: found.label, start: found.start, valueStart: i + 1 });
    minStart = i + 1;
  }

  const head = marks.length ? text.slice(0, marks[0].start) : text;
  const headTokens = head.trim().split(/\s+/).filter(Boolean);
  const code = detectCode(headTokens[0]);
  if (code) {
    out.code = code.code;
    out.codeType = code.codeType;
    headTokens.shift();
  }
  out.title = cleanValue(headTokens.join(' '));

  marks.forEach((m, idx) => {
    const end = idx + 1 < marks.length ? marks[idx + 1].start : text.length;
    const value = cleanValue(text.slice(m.valueStart, end));
    out.items.push({ label: m.label, key: m.key, value });
    if (!value) return;
    if (m.key && m.key !== '기타') {
      out.fields[m.key] = out.fields[m.key] ? out.fields[m.key] + ' / ' + value : value;
    } else {
      out.others.push({ label: m.key === '기타' ? '기타' : m.label, value });
    }
  });

  const food = FOOD_HINT_KEYS.some(k => out.fields[k]);
  const kc = KC_HINT_KEYS.some(k => out.fields[k]);
  out.section = food && !kc ? 'food' : (kc && !food ? 'kc' : '');
  return out;
}

/**
 * 인식 결과를 폼 한 칸(식검 또는 KC)에 맞춘다 — 그 칸에 없는 키는 「기타」 줄로 보낸다(값을 버리지 않는다)
 * @param {ReturnType<typeof parseLabelText>} parsed
 * @param {string[]} keys 그 칸의 키 목록(FOOD_FIELDS / KC_FIELDS 의 key)
 * @returns {Object<string,string>} 채울 값(빈 값은 넣지 않는다)
 */
export function toSection(parsed, keys) {
  const res = {};
  const extra = [];
  Object.keys(parsed.fields || {}).forEach(k => {
    if (keys.indexOf(k) >= 0) res[k] = parsed.fields[k];
    else extra.push(k + ': ' + parsed.fields[k]);
  });
  (parsed.others || []).forEach(o => extra.push(o.label === '기타' ? o.value : o.label + ': ' + o.value));
  if (extra.length && keys.indexOf('기타') >= 0) res['기타'] = extra.join('\n');
  return res;
}

// ---- 식검/KC 선택 · 입력 여부 판정 (2026-10-11 YZY) ----
// 공백만 있으면 비어 있음
export function isFilled(v) {
  return v != null && String(v).trim() !== '';
}
// 식검/KC 중 어느 쪽 영역을 보일지. 모르는 값은 식검(기본)
export function labelMode(v) {
  if (v === 'none') return 'none' // 선택 안 함 — 새 등록·내용 없는 줄의 기본(2026-10-16 YZY): 입력칸을 아무것도 안 보인다
  return v === 'kc' ? 'kc' : (v === 'hyg' ? 'hyg' : 'food');
}
export function showFood(mode) { return labelMode(mode) === 'food'; }
export function showKc(mode) { return labelMode(mode) === 'kc'; }
export function showHyg(mode) { return labelMode(mode) === 'hyg'; } // 위생검역(2026-10-16)
// 저장된 라벨로 선택 초기값 — 아무 내용도 없으면 선택 안 함(none), KC 만 있으면 KC, 위생검역만 있으면 hyg, 그 외(둘 다·식검만)는 식검
export function initialMode(label) {
  const has = s => !!s && Object.keys(s).some(k => isFilled(s[k]));
  if (!label || (!has(label.food) && !has(label.kc) && !has(label.hyg))) return 'none'
  if (has(label.hyg) && !has(label.food) && !has(label.kc)) return 'hyg'
  return has(label.kc) && !has(label.food) ? 'kc' : 'food';
}
